<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:p-6"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-center sm:justify-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiHeadQuestion
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900 select-none"
                                    >
                                        {{ t('buttonInsertAsCondition') }}
                                    </DialogTitle>
                                </div>
                            </div>
                            <!-- Condition builder sources -->
                            <div class="mt-2 flex w-full gap-2">
                                <nav
                                    class="h-96 max-h-96 w-72 max-w-72 min-w-72 rounded-md border-2 border-gray-300"
                                >
                                    <select
                                        id="placeholders"
                                        v-model="selectedPlaceholderGroup"
                                        name="placeholders"
                                        class="block w-full rounded-t-md border-0 border-b border-b-gray-300 bg-white px-3 py-2 font-medium shadow-sm focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                    >
                                        <option
                                            v-for="(placeholder, index) in placeholders"
                                            :key="index"
                                            class="font-medium"
                                            :value="index"
                                        >
                                            {{ placeholder.name }} ({{ placeholder.description }})
                                        </option>
                                    </select>
                                    <ul class="h-80 max-h-80 overflow-y-auto">
                                        <li
                                            v-for="variable in placeholders[
                                                selectedPlaceholderGroup
                                            ].model"
                                            :key="variable"
                                        >
                                            <a
                                                href="#"
                                                class="group block rounded-md p-2 pl-3 text-xs leading-6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                                                :title="
                                                    extractRealDescription(
                                                        placeholders[selectedPlaceholderGroup],
                                                        variable,
                                                    )
                                                "
                                                @click="
                                                    conditionAdd(
                                                        variable,
                                                        placeholders[selectedPlaceholderGroup],
                                                    )
                                                "
                                            >
                                                {{ variable }}
                                            </a>
                                        </li>
                                    </ul>
                                </nav>

                                <!-- Condition builder -->
                                <div class="h-96 max-h-96 flex-1 overflow-y-auto text-gray-600">
                                    <div
                                        v-for="(condition, index) in conditions"
                                        :key="`${condition.name}-${index}`"
                                        class="mb-2 flex items-baseline justify-start gap-0 rounded-md bg-amber-200"
                                    >
                                        <div
                                            class="flex-none self-stretch rounded-l-md pt-2 text-xs"
                                        >
                                            <ActionButton
                                                v-tippy="t('buttonRemoveCondition')"
                                                state="transparent"
                                                :icon="MdiClose"
                                                size="md"
                                                :is-disabled="conditions.length < 2"
                                                @click="conditionRemove(index)"
                                            />
                                        </div>
                                        <div class="w-24 flex-none p-2 text-xs">
                                            <select
                                                id="logical"
                                                v-model="condition.logical"
                                                name="logical"
                                                class="w-20 min-w-20 rounded-md border border-gray-300 px-2 py-0.5 text-xs font-medium shadow-sm focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                            >
                                                <option
                                                    v-for="logical in Object.keys(
                                                        getConditionLogicals(index),
                                                    )"
                                                    :key="logical"
                                                    class="font-medium"
                                                >
                                                    {{ logical }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="grow p-2 text-xs">
                                            {{ condition.name }}
                                        </div>
                                        <div
                                            class="w-24 flex-none self-stretch bg-cyan-200 p-2 text-xs"
                                        >
                                            <select
                                                id="operator"
                                                v-model="condition.glue"
                                                name="operator"
                                                class="w-20 min-w-20 rounded-md border border-gray-300 px-2 py-0.5 text-xs font-medium shadow-sm focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                            >
                                                <option
                                                    v-for="glue in Object.keys(
                                                        getConditionLogicals(1),
                                                    )"
                                                    :key="glue"
                                                    class="font-medium"
                                                    :value="glue"
                                                >
                                                    {{ glue }}
                                                </option>
                                            </select>
                                        </div>
                                        <div
                                            class="w-12 flex-none self-stretch bg-cyan-200 p-2 text-xs"
                                        >
                                            <ActionButton
                                                v-tippy="t('buttonInsertConditionValue')"
                                                state="transparent"
                                                :icon="MdiPlus"
                                                size="md"
                                                :is-disabled="false"
                                                @click="conditionValueAdd(index)"
                                            />
                                        </div>
                                        <div
                                            class="w-96 min-w-96 self-stretch rounded-r-md bg-cyan-200 p-2 text-xs"
                                        >
                                            <div
                                                v-for="(val, vindex) in condition.values"
                                                :key="`${index}-${vindex}`"
                                                class="mr-1 mb-1 flex items-center justify-start gap-0 rounded-md border border-gray-300 bg-white text-xs outline-1"
                                            >
                                                <select
                                                    :id="`condition-relational-${vindex}`"
                                                    v-model="condition.values[vindex].operator"
                                                    name="operator"
                                                    class="w-24 min-w-24 flex-none rounded-l-md border-0 bg-white px-2 py-0.5 text-xs font-medium shadow-sm focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                                >
                                                    <option
                                                        v-for="operator in Object.keys(
                                                            getConditionRelationals(),
                                                        )"
                                                        :key="operator"
                                                        class="font-medium"
                                                    >
                                                        {{ operator }}
                                                    </option>
                                                </select>
                                                <input
                                                    :id="`condition-value-${vindex}`"
                                                    v-model="condition.values[vindex].value"
                                                    placeholder="Fill text value"
                                                    type="text"
                                                    name="key"
                                                    autocomplete="off"
                                                    class="block w-full grow border border-white px-2 py-1 text-xs shadow-sm ring-0 outline-hidden focus:border-white focus:ring-0 focus:ring-offset-0 focus:outline-hidden"
                                                    :disabled="
                                                        condition.values[vindex].operator ===
                                                        'exists'
                                                    "
                                                />
                                                <ActionButton
                                                    v-tippy="t('buttonRemoveConditionValue')"
                                                    state="clean"
                                                    :icon="MdiClose"
                                                    :is-disabled="vindex === 0"
                                                    class-override="rounded-none px-px sm:px-px rounded-r-md"
                                                    @click="conditionValueRemove(index, vindex)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 justify-end gap-4 select-none sm:mt-4 sm:flex">
                                <SwitchGroup
                                    as="div"
                                    class="flex items-center"
                                >
                                    <Switch
                                        v-model="useElse"
                                        :class="[
                                            useElse ? 'bg-emerald-400' : 'bg-gray-200',
                                            conditions.length === 0
                                                ? 'cursor-not-allowed'
                                                : 'cursor-pointer',
                                            'focus:ring-none relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden',
                                        ]"
                                        :disabled="conditions.length === 0"
                                    >
                                        <span
                                            aria-hidden="true"
                                            :class="[
                                                useElse ? 'translate-x-5' : 'translate-x-0',
                                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                                            ]"
                                        />
                                    </Switch>
                                    <SwitchLabel
                                        as="span"
                                        class="ml-3 cursor-pointer text-sm"
                                    >
                                        <span
                                            :class="[
                                                useElse ? 'text-gray-900' : 'text-gray-400',
                                                'font-medium',
                                            ]"
                                        >
                                            {{ t('buttonConditionUseElse') }}
                                        </span>
                                    </SwitchLabel>
                                </SwitchGroup>
                                <ActionButton
                                    :value="t('buttonInsertAsCondition')"
                                    state="primary"
                                    :icon="MdiHeadQuestion"
                                    :is-disabled="conditions.length === 0"
                                    @click="conditionsInsert()"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
    import { reactive, ref } from 'vue';
    import ActionButton from '../ui/ActionButton.vue';
    import {
        Switch,
        SwitchGroup,
        SwitchLabel,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
    } from '@headlessui/vue';
    import { MdiHeadQuestion, MdiClose, MdiPlus } from 'materialdesignicons-vue3/icons/';
    import {
        getConditionLogicals,
        getConditionRelationals,
        createConditionMarkup,
        extractRealDescription,
    } from '../composables/usePlaceholderTypes';

    defineExpose({
        name: 'ConditionEditor',
    });

    const props = defineProps({
        placeholders: {
            type: Object,
            required: true,
        },
        state: {
            type: Object,
            required: true,
        },
    });

    const conditions = reactive([]);
    const placeholders = props.placeholders;
    const selectedPlaceholderGroup = ref(0);

    const state = props.state;

    const getDefaultValue = () => {
        return {
            operator: '==',
            value: '',
        };
    };

    const useElse = ref(false);

    const conditionValueAdd = (index, value = getDefaultValue()) => {
        conditions[index].values.push(value);
    };

    const conditionValueRemove = (index, valueIndex) => {
        conditions[index].values.splice(valueIndex, 1);
    };

    const conditionAdd = (item, group) => {
        const proto = {
            name: item,
            group: group.name,
            logical: conditions.length === 0 ? 'IF' : 'AND',
            glue: 'AND',
            type: group.type,
            values: [getDefaultValue()],
        };
        conditions.push(proto);
    };

    const conditionRemove = (index) => {
        conditions.splice(index, 1);
        conditions[0].logical = 'IF';
    };

    const conditionsInsert = () => {
        const str = createConditionMarkup(conditions, useElse.value).join('');
        state.action(str);
        state.open = false;
    };

    const dialogClose = (preventClose = false) => {
        if (preventClose === false) {
            state.open = false;
        }
    };
</script>
